/**
 * high-pass.ts describes a high-pass filter meeting the provided specifications.
 * It provides all the methods promised by `FilterSignature`.
 * It uses wide-band or narrow-band approaches, depending on how wide the passband is.
 * In a narrow-band implementation, each stage helps shape both skirts.
 * Like low-pass or high-pass circuits, a narrow-band band-pass filter can only have five stages.
 * In contrast, a wide-band implementation uses a combination of high-pass and low-pass stages to shape the skirts independently.
 * A wide-band filter can have up to six stages, three for each skirt.
 */

import FilterConstants from '../../filter-constants';
import WideBand from './band-pass/wide-band';
import NarrowBand from './band-pass/narrow-band';
import FrequencyRange from '../process-data/frequency';
import MathUtils from '../utils/math';
import { Complex, createComplex } from '../process-data/complex';
import { createTimeAxis } from '../formulas/time-axis';
import { SpecificationParameters, FilterSignature, FilterStage, StepResponse } from './filter';

export default {
  get name(): string {
    return FilterConstants.filterType.bandPass;
  },

  getStages(specificationParameters: SpecificationParameters): FilterStage[] {
    let stages = [];
    if (2.5 * specificationParameters.centerFrequency < specificationParameters.passbandFrequency) {
      stages = WideBand.getStages(specificationParameters);
    } else {
      stages = NarrowBand.getStages(specificationParameters);
    }

    const stageLetterArray = FilterConstants.stageLetter;
    const result = stages.map((item: FilterStage, index: number) => {
      item.stageLetter = stageLetterArray[index] ?? '';
      item.stageId = index + 1;

      return item;
    });

    return result;
  },

  getOrder(specificationParameters: SpecificationParameters): number {
    let result = -1;
    if (2.5 * specificationParameters.centerFrequency < specificationParameters.passbandFrequency) {
      result = WideBand.getOrder(specificationParameters);
    } else {
      result = NarrowBand.getOrder(specificationParameters);
    }

    return result;
  },

  getNumberOfStages(specificationParameters: SpecificationParameters): number {
    let result = -1;
    if (2.5 * specificationParameters.centerFrequency < specificationParameters.passbandFrequency) {
      result = WideBand.getNumberOfStages(specificationParameters);
    } else {
      result = NarrowBand.getNumberOfStages(specificationParameters);
    }

    return result;
  },

  getFilterFrequencyRange(
    stopbandFrequency: number,
    _passbandFrequency?: number,
    centerFrequency?: number,
  ): FrequencyRange {
    const centerFreq = centerFrequency ?? 0;
    const startFrequency =
      Math.sqrt(stopbandFrequency * stopbandFrequency + centerFreq * centerFreq) - stopbandFrequency;
    const stopFrequency = (centerFreq * centerFreq) / startFrequency;
    const pointsPerDecade = 200 / MathUtils.logBaseTen(stopFrequency / startFrequency);
    const decade = Math.log10(stopFrequency) - Math.log10(startFrequency);

    const result = FrequencyRange.create({
      startFrequency: startFrequency,
      stopFrequency: stopFrequency,
      pointsPerDecade: pointsPerDecade,
      decade: decade,
    });
    return result;
  },

  getActualStageSpecFrequency(frequencyArray: FrequencyRange): FrequencyRange {
    const startFrequency = frequencyArray.startFrequency;
    const stopFrequency = frequencyArray.stopFrequency;
    const pointsPerDecade = 100 / MathUtils.logBaseTen(stopFrequency / startFrequency);

    const result = FrequencyRange.create({
      startFrequency: startFrequency,
      stopFrequency: stopFrequency,
      pointsPerDecade: pointsPerDecade,
    });
    return result;
  },

  createFirstOrderFilterAmplitude(): void {
    throw 'No implementation for band pass first order stages';
  },

  createSecondOrderFilterAmplitude(fp: number, Q: number, gain: number, frequencyArray: number[]): Complex[] {
    return frequencyArray.map((frequency) => {
      const f = frequency;
      const complexGain = createComplex(gain, 0);
      const complexNum = complexGain.mult(createComplex(0, (f * fp) / Q));
      const complexDenom = createComplex(fp * fp - f * f, (f * fp) / Q);
      const res = complexNum.divide(complexDenom);

      return res;
    });
  },

  createTimeAxis(passbandFrequency: number, stopbandFrequency: number): number[] {
    const fCutOff = Math.sqrt(passbandFrequency * stopbandFrequency) / 5;
    return createTimeAxis(fCutOff);
  },

  setupFirstOrderStepResponse(): void {
    throw 'No implementation for band pass first order stages';
  },

  setupSecondOrderStepResponse(stageFp: number, stageQ: number, gain: number): StepResponse {
    const result = { w0: stageFp * 2 * Math.PI, Q: stageQ, a: 0, b: 0, c: 0 };
    result.b = (gain * result.w0) / result.Q;
    return result;
  },

  getImpulseArrayFirstElement(): number {
    return 0;
  },

  getSecondOrderStageImplementation(stageFp: number, stageQ: number, gain: number): string {
    let result = FilterConstants.implementationType.deliyannisFriend;
    if (
      stageFp * gain * (0.5 + stageQ) > FilterConstants.Limits.INDUCTOR_PREFERRED_FREQ_X_GAIN_X_Q &&
      stageFp > FilterConstants.Limits.INDUCTOR_ALLOWED_FREQ
    ) {
      result = FilterConstants.implementationType.inductor;
    }

    return result;
  },

  //getMaxFrequency used in voltage range calculations.  We don't look at magnitudes beyond this frequency for voltage range because we don't care if they get clipped.
  getMaxFrequency(stopbandFrequency: number, _frequencyArray?: number[], stageFp?: number): number {
    const fp = stageFp ?? 0;
    return (stopbandFrequency + Math.sqrt(stopbandFrequency * stopbandFrequency + 4 * fp * fp)) / 2;
  },

  getAdcFrequency(stopFrequency: number): number {
    return stopFrequency;
  },
} as FilterSignature;
